import { Dispatch, SetStateAction, useCallback } from "react";

export const shouldConfirmUnsavedStepExit = (
  currentStep: number,
  targetStep: number,
  completedSteps: Set<number>
) => targetStep !== currentStep && !completedSteps.has(currentStep);

export const markVisitedStepIncomplete = (
  completedSteps: Set<number>,
  stepIndex: number
) => {
  if (!completedSteps.has(stepIndex)) return completedSteps;
  const next = new Set(completedSteps);
  next.delete(stepIndex);
  return next;
};

interface UnsavedStepNavigationArgs {
  currentStep: number;
  completedSteps: Set<number>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  setCompletedSteps: Dispatch<SetStateAction<Set<number>>>;
  confirmMessage: string;
}

export const useUnsavedAffordableStepNavigation = ({
  currentStep,
  completedSteps,
  setCurrentStep,
  setCompletedSteps,
  confirmMessage,
}: UnsavedStepNavigationArgs) => {
  const navigateToStep = useCallback(
    (targetStep: number) => {
      if (targetStep === currentStep) return;
      if (
        shouldConfirmUnsavedStepExit(currentStep, targetStep, completedSteps) &&
        !window.confirm(confirmMessage)
      ) {
        return;
      }

      setCompletedSteps((prev) => markVisitedStepIncomplete(prev, targetStep));
      setCurrentStep(targetStep);
    },
    [
      completedSteps,
      confirmMessage,
      currentStep,
      setCompletedSteps,
      setCurrentStep,
    ]
  );

  const navigateBack = useCallback(() => {
    if (currentStep > 0) navigateToStep(currentStep - 1);
  }, [currentStep, navigateToStep]);

  return { navigateBack, navigateToStep };
};
